import { Suspense, useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { BarChart3, BriefcaseBusiness, CalendarCheck2, CircleDollarSign, ClipboardList, Grid2X2, LayoutDashboard, PiggyBank, Plus, ReceiptText, Settings, ShieldCheck, Target, WalletCards, X } from 'lucide-react'
import { QuickAdd } from './QuickAdd'
import { BrandLogo } from './BrandLogo'
import { ProfileAvatar } from './ProfileAvatar'
import { useAuth } from '../state/auth'
import { supabase } from '../lib/supabase'

const primaryNav = [
  { to: '/app', label: 'Ringkasan', icon: LayoutDashboard, end: true },
  { to: '/app/transactions', label: 'Transaksi', icon: ReceiptText },
  { to: '/app/accounts', label: 'Dompet', icon: WalletCards },
  { to: '/app/assets', label: 'Tabungan & aset', icon: PiggyBank },
  { to: '/app/budgets', label: 'Anggaran', icon: Target },
  { to: '/app/bills', label: 'Tagihan', icon: CalendarCheck2 },
  { to: '/app/salary', label: 'Gaji', icon: CircleDollarSign },
  { to: '/app/freelance', label: 'Freelance', icon: BriefcaseBusiness },
  { to: '/app/reports', label: 'Laporan', icon: BarChart3 },
]

const mobileMainNav = [
  { to: '/app', label: 'Ringkasan', icon: LayoutDashboard, end: true },
  { to: '/app/transactions', label: 'Transaksi', icon: ReceiptText },
  { to: '/app/budgets', label: 'Anggaran', icon: Target },
]

const mobileMoreBase = [
  { to: '/app/accounts', label: 'Dompet', icon: WalletCards },
  { to: '/app/assets', label: 'Tabungan & aset', icon: PiggyBank },
  { to: '/app/bills', label: 'Tagihan', icon: CalendarCheck2 },
  { to: '/app/salary', label: 'Gaji', icon: CircleDollarSign },
  { to: '/app/freelance', label: 'Freelance', icon: BriefcaseBusiness },
  { to: '/app/reports', label: 'Laporan', icon: BarChart3 },
  { to: '/app/settings', label: 'Pengaturan', icon: Settings },
]

export function AppShell() {
  const { access, user } = useAuth()
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [profileName, setProfileName] = useState('')
  const [profileAvatar, setProfileAvatar] = useState('blue')
  const location = useLocation()

  useEffect(() => {
    if (!supabase || !user) return
    const client = supabase
    const userId = user.id
    let active = true
    async function loadProfile() {
      const { data } = await client.from('profiles').select('display_name,avatar_id').eq('owner_id', userId).single()
      if (active && data) { if (data.display_name) setProfileName(data.display_name); setProfileAvatar(data.avatar_id || 'blue') }
    }
    void loadProfile()
    const refresh = () => { void loadProfile() }
    window.addEventListener('smart-finance:profile-updated', refresh)
    return () => { active = false; window.removeEventListener('smart-finance:profile-updated', refresh) }
  }, [user])

  useEffect(() => {
    if (!moreOpen) return
    const previousOverflow = document.body.style.overflow
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setMoreOpen(false) }
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [moreOpen])

  const displayName = profileName || String(user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Kamu')
  const avatarLetter = displayName.trim().charAt(0).toUpperCase() || 'D'
  const isStaff = access?.role === 'admin' || access?.role === 'developer'
  const staffNav = isStaff ? [
    { to: '/app/admin', label: 'Admin', icon: ShieldCheck },
    { to: '/app/audit', label: 'Riwayat audit', icon: ClipboardList },
  ] : []
  const moreNav = [...mobileMoreBase, ...staffNav]
  const moreRouteActive = moreNav.some(({ to }) => location.pathname === to || location.pathname.startsWith(`${to}/`))
  const roleLabel = access?.role === 'developer' ? 'Developer' : access?.role === 'admin' ? 'Admin' : 'Profil & pengaturan'

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar__brand"><BrandLogo /><span><strong>Dhuwit</strong><small>Keuangan jadi mudah</small></span></div>
        <nav className="sidebar__nav" aria-label="Navigasi utama">
          {primaryNav.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end}><Icon size={19} /><span>{label}</span></NavLink>)}
        </nav>
        {staffNav.length ? <nav className="sidebar__nav sidebar__nav--secondary" aria-label="Navigasi pengelola">
          {staffNav.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to}><Icon size={19} /><span>{label}</span></NavLink>)}
        </nav> : <div className="sidebar__spacer" />}
        <NavLink className="sidebar__profile" to="/app/settings" aria-label={`Profil dan pengaturan ${displayName}`}><ProfileAvatar avatarId={profileAvatar} letter={avatarLetter} /><span><strong>{displayName}</strong><small>{roleLabel}</small></span></NavLink>
        <a className="sidebar__studio" href="https://apabaestudio.com" target="_blank" rel="noreferrer">Developed by apabaestudio.com</a>
      </aside>
      <main className="main-content" key={location.pathname}>
        <Suspense fallback={<div className="page"><p className="muted">Membuka halaman…</p></div>}><Outlet context={{ profileName: displayName, avatarLetter, avatarId: profileAvatar }} /></Suspense>
      </main>
      <button className="floating-add" onClick={() => setQuickAddOpen(true)}><Plus size={20} /><span>Catat transaksi</span></button>
      <nav className="mobile-bottom-nav" aria-label="Navigasi mobile">
        {mobileMainNav.slice(0, 2).map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end}><Icon size={21} /><span>{label}</span></NavLink>)}
        <button className="mobile-bottom-nav__add" aria-label="Catat transaksi" onClick={() => setQuickAddOpen(true)}><span><Plus size={27} /></span><small>Catat</small></button>
        {mobileMainNav.slice(2).map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end}><Icon size={21} /><span>{label}</span></NavLink>)}
        <button className={`mobile-bottom-nav__more ${moreOpen || moreRouteActive ? 'active' : ''}`} aria-label="Buka menu lainnya" aria-expanded={moreOpen} onClick={() => setMoreOpen(true)}><Grid2X2 size={21} /><span>Lainnya</span></button>
      </nav>
      {moreOpen ? <div className="mobile-more-layer">
        <button className="mobile-more__backdrop" aria-label="Tutup menu lainnya" onClick={() => setMoreOpen(false)} />
        <section className="mobile-more" role="dialog" aria-modal="true" aria-labelledby="mobile-more-title">
          <header><div><span>Menu</span><h2 id="mobile-more-title">Lainnya</h2></div><button aria-label="Tutup menu lainnya" onClick={() => setMoreOpen(false)}><X size={20} /></button></header>
          <nav aria-label="Menu lainnya">{moreNav.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} onClick={() => setMoreOpen(false)}><span><Icon size={19} /></span><strong>{label}</strong></NavLink>)}</nav>
          <footer><span>{roleLabel === 'Profil & pengaturan' ? displayName : `Akun ${roleLabel}`}</span><a href="https://apabaestudio.com" target="_blank" rel="noreferrer">Developed by apabaestudio.com</a></footer>
        </section>
      </div> : null}
      <QuickAdd open={quickAddOpen} onClose={() => setQuickAddOpen(false)} />
    </div>
  )
}
